import type { Turn } from "../domain/rules.js";
import { replay } from "../domain/rules.js";
import type { Game, Group, State } from "../domain/store.js";

/**
 * Reading the server back (the other half of D-6).
 *
 * Strictly additive: it only ever *adds* groups, players and finished games the
 * device does not already have. It never deletes, never renames and never
 * rewrites a turn. That keeps it free of the merge question D-6 leaves open --
 * what to do when one group was edited on two devices - while still solving the
 * case that actually loses data today: a device that has none, because the
 * phone was replaced or the browser storage was cleared.
 */

export interface PullDeps {
  baseUrl?: string;
  token: () => string | null;
  fetch?: typeof globalThis.fetch;
}

interface ServerGroup {
  id: string;
  name: string;
}
interface ServerPlayer {
  id: string;
  display_name: string;
}
interface ServerGameRow {
  id: string;
  client_id: string;
}
interface ServerTurn {
  person_id: string;
  type: "bank" | "strike";
  points: number;
  created_at: number;
}

export interface Pulled {
  groups: Group[];
  games: Game[];
}

export async function pullEverything(deps: PullDeps): Promise<Pulled | null> {
  const base = deps.baseUrl ?? "/api/kniffel";
  // Bound: a browser's fetch rejects being called off the window object.
  const doFetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const token = deps.token();
  if (!token) return null;

  async function get<T>(path: string): Promise<T | null> {
    try {
      const res = await doFetch(`${base}${path}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return (await res.json()) as T;
    } catch {
      return null;
    }
  }

  const groupList = await get<{ groups: ServerGroup[] }>("/groups");
  if (!groupList) return null;

  const groups: Group[] = [];
  const games: Game[] = [];

  for (const g of groupList.groups) {
    const players = await get<{ players: ServerPlayer[] }>(`/groups/${g.id}/players`);
    groups.push({
      id: g.id,
      name: g.name,
      players: (players?.players ?? []).map((p) => ({ id: p.id, name: p.display_name })),
    });

    const gameList = await get<{ games: ServerGameRow[] }>(`/groups/${g.id}/games`);
    for (const row of gameList?.games ?? []) {
      const detail = await get<{ order: string[]; turns: ServerTurn[] }>(`/games/${row.id}`);
      if (!detail) continue;
      const turns: Turn[] = detail.turns.map((t) => ({
        playerId: t.person_id,
        type: t.type,
        points: t.points,
      }));
      // Only finished games come across. An unfinished one belongs to whichever
      // device is still scoring it, and adopting it here would fight them for
      // the turn order.
      if (!replay(detail.order, turns).finished) continue;
      games.push({
        id: row.id,
        groupId: g.id,
        order: detail.order,
        turns,
        startedAt: detail.turns[0]?.created_at ?? 0,
        finishedAt: detail.turns.at(-1)?.created_at ?? 0,
        // Carried so a device that created this game can recognise its own.
        ...(row.client_id ? { clientId: row.client_id } : {}),
      });
    }
  }

  return { groups, games };
}

export interface MergeReport {
  groupsAdded: number;
  playersAdded: number;
  gamesAdded: number;
}

const norm = (name: string): string =>
  name.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Fold what the server has into what the device has, adding only.
 *
 * A game is recognised as one we already hold by its own id *or* by the client
 * id it was created with -- the same game has both, depending on which device
 * is looking at it.
 */
export function mergePulled(state: State, pulled: Pulled): { state: State; report: MergeReport } {
  const report: MergeReport = { groupsAdded: 0, playersAdded: 0, gamesAdded: 0 };

  const byId = new Map(state.groups.map((g) => [g.id, g]));
  const groups = [...state.groups];

  for (const incoming of pulled.groups) {
    const existing = byId.get(incoming.id);
    if (!existing) {
      groups.push(incoming);
      report.groupsAdded += 1;
      report.playersAdded += incoming.players.length;
      continue;
    }
    // Known group: add players it does not have, leave its name alone.
    const known = new Set(existing.players.map((p) => norm(p.name)));
    const extra = incoming.players.filter((p) => !known.has(norm(p.name)));
    if (extra.length > 0) {
      const index = groups.indexOf(existing);
      groups[index] = { ...existing, players: [...existing.players, ...extra] };
      report.playersAdded += extra.length;
    }
  }

  const heldIds = new Set<string>();
  for (const g of [...state.history, ...(state.game ? [state.game] : [])]) {
    heldIds.add(g.id);
  }
  const fresh = pulled.games.filter((g) => {
    const clientId = (g as Game & { clientId?: string }).clientId;
    return !heldIds.has(g.id) && !(clientId && heldIds.has(clientId));
  });
  report.gamesAdded = fresh.length;

  const history = [...state.history, ...fresh.map(({ ...g }) => g)].sort(
    (a, b) => (b.finishedAt ?? b.startedAt) - (a.finishedAt ?? a.startedAt),
  );

  return { state: { ...state, groups, history }, report };
}
